/**
 * Resume API Client
 * Handles Resume Worker async job pipeline endpoints
 */

import { apiClient } from '../api-client';
import type { 
  Resume, 
  ResumeText, 
  ResumeJob, 
  RenderProgress, 
  ResumeTag 
} from '../../types';

export class ResumeApi {
  /**
   * Get all resumes for user
   */
  async getResumes(userId: string): Promise<Resume[]> {
    const response = await apiClient.get(`/resumes?user_id=${userId}`);
    return response.data;
  }

  /**
   * Get resume by ID
   */
  async getResume(resumeId: string): Promise<Resume> {
    const response = await apiClient.get(`/resumes/${resumeId}`);
    return response.data;
  }

  /**
   * Upload new resume
   */
  async uploadResume(file: File, options?: {
    make_active?: boolean;
  }): Promise<{ resume_id: string; upload_url?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.make_active) {
      formData.append('make_active', 'true');
    }

    const response = await apiClient.post('/resumes/upload', formData);
    return response.data;
  }

  /**
   * Delete resume
   */
  async deleteResume(resumeId: string): Promise<void> {
    await apiClient.delete(`/resumes/${resumeId}`);
  }

  /**
   * Set resume as active
   */
  async setActiveResume(resumeId: string): Promise<void> {
    await apiClient.post(`/resumes/${resumeId}/activate`);
  }

  /**
   * Get resume text content
   */
  async getResumeText(resumeId: string): Promise<ResumeText> {
    const response = await apiClient.get(`/resumes/${resumeId}/text`);
    return response.data;
  }

  /**
   * Update resume text content
   */
  async updateResumeText(resumeId: string, content: string): Promise<ResumeText> {
    const response = await apiClient.patch(`/resumes/${resumeId}/text`, {
      content,
    });
    return response.data;
  }

  /**
   * Trigger resume parsing job
   */
  async parseResume(resumeId: string): Promise<{ job_id: string }> {
    const response = await apiClient.post(`/resumes/${resumeId}/parse`);
    return response.data;
  }

  /**
   * Trigger resume tagging job
   */
  async tagResume(resumeId: string): Promise<{ job_id: string }> {
    const response = await apiClient.post(`/resumes/${resumeId}/tag`);
    return response.data;
  }

  /**
   * Trigger PDF rendering job
   */
  async renderResume(resumeId: string, options?: {
    template?: string;
    format?: 'pdf' | 'docx';
  }): Promise<{ job_id: string }> {
    const response = await apiClient.post(`/resumes/${resumeId}/render`, options || {});
    return response.data;
  }

  /**
   * Get resume job status
   */
  async getJobStatus(jobId: string): Promise<ResumeJob> {
    const response = await apiClient.get(`/resumes/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Get render progress (if available)
   */
  async getRenderProgress(resumeId: string): Promise<RenderProgress | null> {
    try {
      const response = await apiClient.get(`/resumes/${resumeId}/render-progress`);
      return response.data;
    } catch (error) {
      // Progress may not be available for all jobs
      return null;
    }
  }

  /**
   * Get resume tags
   */
  async getResumeTags(resumeId: string): Promise<ResumeTag[]> {
    const response = await apiClient.get(`/resumes/${resumeId}/tags`);
    return response.data;
  }

  /**
   * Download original resume file
   */
  async downloadOriginal(resumeId: string): Promise<Blob> {
    const response = await apiClient.get(`/resumes/${resumeId}/download/original`, {
      headers: { 'Accept': 'application/octet-stream' },
    });
    
    // Convert response to blob for download
    if (response instanceof Blob) {
      return response;
    }
    
    // Fallback for non-blob response
    const blob = new Blob([response], { type: 'application/octet-stream' });
    return blob;
  }

  /**
   * Download generated PDF
   */
  async downloadGenerated(resumeId: string): Promise<Blob> {
    const response = await apiClient.get(`/resumes/${resumeId}/download/generated`, {
      headers: { 'Accept': 'application/pdf' },
    });
    
    // Convert response to blob for download
    if (response instanceof Blob) {
      return response;
    }
    
    // Fallback for non-blob response  
    const blob = new Blob([response], { type: 'application/pdf' });
    return blob;
  }

  /**
   * Get all resume jobs for debugging
   */
  async getResumeJobs(resumeId: string): Promise<ResumeJob[]> {
    const response = await apiClient.get(`/resumes/${resumeId}/jobs`);
    return response.data;
  }

  /**
   * Retry failed job
   */
  async retryJob(jobId: string): Promise<{ job_id: string }> {
    const response = await apiClient.post(`/resumes/jobs/${jobId}/retry`);
    return response.data;
  }

  /**
   * Cancel running job
   */
  async cancelJob(jobId: string): Promise<void> {
    await apiClient.delete(`/resumes/jobs/${jobId}`);
  }
}

export const resumeApi = new ResumeApi();